import axios from '@/_utils/axios';
import { checkIfUrlIsValid, getLineageMetaTitle, joinWithAnd } from '@/_utils/utils';
import { PACKAGING_TOOLBOX_OPTIONS_LIST } from '@/components/Packaging/constants';
import { useTimelineValidation } from '@/components/Packaging/hooks/useTimelineValidation.hook';
import TimelineDndContext from '@/components/Packaging/LayeredTimeline/TimelineDndContext';
import { preparePackagingForSave } from '@/components/Packaging/utils';
import { LoadingStatuses } from '@/constants/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  selectIsPackagingDraft,
  selectIsReadOnlyPackaging,
  selectPackagingActiveHover,
  selectPackagingActiveTemplate,
  selectPackagingContentAssembly,
  selectPackagingLoadingStatus
} from '@/redux/selectors/packagingSelectors';
import { selectCompleteTimelineTemplateFormat, selectTimelineOverlays, selectTimelineSegments } from '@/redux/selectors/packagingTimelineSelectors';
import { setModuleState } from '@/redux/slices/dataTableSlice';
import { getContentAssemblyObject, setToolboxOptions } from '@/redux/slices/packagingSlice';
import { AssemblyInputsDataRequest } from '@/redux/slices/packagingSlice.types';
import { clearAssemblyTemplate, setOverlays, setSegments } from '@/redux/slices/packagingTimelineSlice';
import { SegmentService as PlayerAPI } from '@accurate-player/accurate-player-segment';
import styled from '@emotion/styled';
import { Container, Divider, Grid, Typography, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ActionCreators } from 'redux-undo';
import { Dialog } from '../_common';
import PolarisIcon from '../_common/PolarisIcon';
import Spinner from '../_common/Spinner';
import StepperButton from '../_common/StepperButton';
import { useToast } from '../_common/Toast';
import PosterDisplay from '../IngestManagement/PosterDisplay';
import ActiveHoverDisplay from './ActiveHoverDisplay';
import AssemblyTemplateActionButton from './AssemblyTemplateActionButton';
import AssemblyTemplateSwapButton from './AssemblyTemplateSwapButton';
import ComponentList from './ComponentList/ComponentList';
import InventoryAssignmentModal from './InventoryAssignmentModal/InventoryAssignmentModal';
import LayeredTimeline from './LayeredTimeline/LayeredTimeline';
import PackagingBreadcrumb from './PackagingBreadcrumb';
import PackagingMetadataBar from './PackagingMetadataBar';
import TimelineViewManager from './TimelineViewManager';
import ToolboxDrawer from './ToolboxDrawer';
import PreviewUrlLoader from './VideoPreview/PreviewUrlLoader';
import VideoPreview from './VideoPreview/VideoPreview';

export enum PackagingViewTypes {
  TIMELINE = 'timeline',
  LIST = 'list'
}

type PackagingSubmissionPayload = { id: number; data: AssemblyInputsDataRequest };

const Packaging = () => {
  const dispatch = useAppDispatch();
  const [view, setView] = useState<PackagingViewTypes>(PackagingViewTypes.TIMELINE);
  const playerApiRef = useRef<PlayerAPI | null>(null);
  const activeTemplate = useAppSelector(selectPackagingActiveTemplate);
  const segments = useAppSelector(selectTimelineSegments);
  const overlays = useAppSelector(selectTimelineOverlays);
  const { segments: segmentsTemplate, overlays: overlaysTemplateLayers } = useAppSelector(selectCompleteTimelineTemplateFormat);
  const [videoPreviewKey, setVideoPreviewKey] = useState(0);
  const { id: taskId } = useParams<{ id: string }>();
  const contentAssembly = useAppSelector(selectPackagingContentAssembly);
  const isDraft = useAppSelector(selectIsPackagingDraft);
  const { success, error } = useToast();
  const history = useHistory();
  const theme = useTheme();
  const activeHover = useAppSelector(selectPackagingActiveHover);
  const loadingStatus = useAppSelector(selectPackagingLoadingStatus);
  const isLoadingStatusFulfilled = loadingStatus === LoadingStatuses.fulfilled;
  const isLoadingStatusSettled = isLoadingStatusFulfilled || loadingStatus === LoadingStatuses.rejected;
  const [isSubmitting, setIsSubmitting] = useState({ state: false, message: '' });
  const [showUnassignedSegmentsDialog, setShowUnassignedSegmentsDialog] = useState(false);
  const isReadOnly = useAppSelector(selectIsReadOnlyPackaging);

  const [urlToUse, setUrlToUse] = useState<string | null>(null);
  const lineageMetaTitle = getLineageMetaTitle(contentAssembly?.version);
  const seriesName = contentAssembly?.version?.lineage_meta?.series_name || '';
  const titleWithoutSeries = seriesName
    ? lineageMetaTitle
        .replace(seriesName, '')
        .replace(/^\s*:?\s*/, '')
        .trim()
    : lineageMetaTitle;

  const showGoBackDialog = useMemo(
    () => !!(contentAssembly && !contentAssembly.assigned_user && !isLoadingStatusSettled),
    [contentAssembly, isLoadingStatusSettled]
  );

  const { isCompleteButtonDisabled, isSaveDraftButtonDisabled, completeButtonTooltip, saveDraftButtonTooltip, unassignedOptionalSegmentsLabels } =
    useTimelineValidation({
      segments,
      overlays,
      isLoadingStatusFulfilled,
      isSubmitting: isSubmitting.state
    });

  useEffect(() => {
    dispatch(setToolboxOptions(PACKAGING_TOOLBOX_OPTIONS_LIST));
    dispatch(getContentAssemblyObject(taskId));
    dispatch(setModuleState('packaging')); // This is needed to store titles search history in user preferences under the "packaging" namespace

    return () => {
      dispatch(setToolboxOptions([]));
      dispatch(clearAssemblyTemplate());
      dispatch(setModuleState(null));
    };
  }, []);

  useEffect(() => {
    if (activeTemplate) {
      dispatch(setSegments(activeTemplate.data.segments));
      dispatch(setOverlays(activeTemplate.data.overlays));
      dispatch(ActionCreators.clearHistory()); // reset history after all data is loaded and commited to the store
    }
  }, [activeTemplate]);

  useEffect(() => {
    setVideoPreviewKey((state) => state + 1);
  }, [segments]);

  useEffect(() => {
    if (contentAssembly) {
      const retrieveThumbnail = async () => {
        let workingUrl = null;

        const fallbackUrls = [contentAssembly?.version?.thumbnail_link, theme?.preview_placeholder, theme?.logo];

        if (contentAssembly?.version?.thumbnail_link) {
          fallbackUrls.unshift(contentAssembly?.version?.thumbnail_link);
        }

        for (const url of fallbackUrls) {
          if (url) {
            const isValid = await checkIfUrlIsValid(url);
            if (isValid) {
              workingUrl = url;
              break;
            }
          }
        }

        setUrlToUse(workingUrl);
      };

      retrieveThumbnail();
    }
  }, [contentAssembly]);

  const toggleView = (view: PackagingViewTypes) => {
    setView(view);
  };

  const handleSaveAsDraft = () => {
    if (!activeTemplate?.id) {
      error('Please select a template before saving as draft');
      return;
    }

    setIsSubmitting({ state: true, message: 'Saving as draft...' });
    axios
      .post<unknown, AxiosResponse<unknown>, PackagingSubmissionPayload>(`/content_assembly/${taskId}/`, {
        id: activeTemplate.id,
        data: preparePackagingForSave(segments, overlays)
      })
      .then(() => {
        success('Successfully saved as draft');
        history.push(`/tasks/?id=${taskId}`);
      })
      .catch(() => {
        error('Failed to save as draft');
      })
      .finally(() => {
        setIsSubmitting({ state: false, message: '' });
      });
  };

  const handleCompleteButtonClick = () => {
    if (!activeTemplate?.id) {
      error('Please select a template before saving');
      return;
    }

    if (unassignedOptionalSegmentsLabels.length) {
      setShowUnassignedSegmentsDialog(true);
    } else {
      submitPackagingTimeline();
    }
  };

  const submitPackagingTimeline = () => {
    if (!activeTemplate?.id) {
      error('Please select a template before saving');
      return;
    }

    setIsSubmitting({ state: true, message: 'Submitting...' });
    axios
      .post<unknown, AxiosResponse<unknown>, PackagingSubmissionPayload>(`/content_assembly/${taskId}/submit/`, {
        id: activeTemplate.id,
        data: preparePackagingForSave(segments, overlays)
      })
      .then(() => {
        success('Task submitted successfully');
        history.push(`/tasks/?id=${taskId}`);
      })
      .catch(() => {
        error('Failed to complete task');
      })
      .finally(() => {
        setIsSubmitting({ state: false, message: '' });
      });
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push('/tasks');
    }
  };

  return (
    <div>
      {!isLoadingStatusSettled && <StyledSpinner size={200} contained label="Loading Task..." />}
      {isSubmitting.state && <StyledSpinner size={200} contained label={isSubmitting.message} />}

      <PackagingHeaderContainer>
        <Grid container justifyContent="space-between" alignItems="center">
          <PackagingBreadcrumb />
          <Grid item>
            <TemplateControls container justifyContent="flex-end" alignItems="center">
              <Typography variant="h4">
                Template: {activeTemplate ? activeTemplate.name || '-' : <PolarisIcon icon="loading_dots" height={30} style={{ marginRight: 10 }} />}
              </Typography>
              {(!isReadOnly || contentAssembly) && <Divider orientation="vertical" sx={{ height: 28, mx: 1 }} />}
              {!isReadOnly && <AssemblyTemplateSwapButton />}
              {contentAssembly && (
                <AssemblyTemplateActionButton
                  defaultName={contentAssembly?.hid || '-'}
                  timeline={{ segments: segmentsTemplate, overlays: overlaysTemplateLayers }}
                  autoApply={!isReadOnly}
                />
              )}
            </TemplateControls>
          </Grid>
        </Grid>
        <PackagingMetadataBar />
      </PackagingHeaderContainer>
      <TimelineViewManager toggleView={toggleView}>
        <StyledPosterDisplay url={urlToUse || ''} filename="poster.jpg" height={116} width={80} />
        <TitleDescription>
          <Rating>{contentAssembly?.extra_meta?.v_chip_rating || 'No Rating'}</Rating>
          <Typography variant="h3" sx={{ margin: 0, padding: 0 }}>
            {seriesName}
          </Typography>
          <Typography variant="body1" sx={{ paddingTop: 0.5 }}>
            {titleWithoutSeries}
          </Typography>
        </TitleDescription>
      </TimelineViewManager>

      <PreviewUrlLoader>
        {(isLoading, retryLoadingFailedPreview) => (
          <VideoPreview
            /**
             * `videoPreviewKey` is changed every time the segment array is changed (segments are added, removed, or reordered).
             * This is done to make sure that VideoPreview's DOM tree is re-rendered completely.
             * Under the hood segment player generates additional <video /> elements (to facilitate smooth segment transitions).
             * If we don't re-render the VideoPreview component completely, SegmentPlayer will use a generated <video /> element
             * which would mean the count of <video /> elements increases with every segment change.
             */
            key={videoPreviewKey}
            playerApiRef={playerApiRef}
            isLoadingPreviewURLs={isLoading}
            retryLoadingFailedPreview={retryLoadingFailedPreview}
          />
        )}
      </PreviewUrlLoader>
      {activeHover && <ActiveHoverDisplay data={activeHover} />}

      {view === PackagingViewTypes.TIMELINE ? (
        <TimelineDndContext>
          {({ isDraggingNewSegment, isDraggingNewOverlay }) => (
            <>
              <LayeredTimeline playerApiRef={playerApiRef} isDraggingNewSegment={isDraggingNewSegment} isDraggingNewOverlay={isDraggingNewOverlay} />
              {!isReadOnly && <ToolboxDrawer />}
              {isDraft && !isReadOnly && (
                <StepperButton
                  complete={{
                    label: 'Complete',
                    disabled: isCompleteButtonDisabled,
                    tooltip: completeButtonTooltip,
                    onClick: handleCompleteButtonClick
                  }}
                  draft={{
                    label: 'Save as Draft',
                    disabled: isSaveDraftButtonDisabled,
                    tooltip: saveDraftButtonTooltip,
                    onClick: handleSaveAsDraft
                  }}
                />
              )}
            </>
          )}
        </TimelineDndContext>
      ) : (
        <ComponentList />
      )}
      <div style={{ height: 145 }} />

      {contentAssembly && (
        <InventoryAssignmentModal initialTitleId={contentAssembly.job.title.id} initialVersionId={contentAssembly.job.version?.id} />
      )}
      <Dialog
        titleIcon={<StyledWarningIcon icon="error" height={25} />}
        onSubmit={handleGoBack}
        onCancel={handleGoBack}
        open={showGoBackDialog}
        title="Tasks must be assigned"
        hideCancel
        submitText="Go Back"
        showX={false}
      >
        <div>Tasks must be assigned in order to use the packaging module.</div>
        <div>Please assign a user to the task before using the packaging module.</div>
      </Dialog>

      <Dialog
        title="Some Segments are Missing"
        open={showUnassignedSegmentsDialog}
        onCancel={() => setShowUnassignedSegmentsDialog(false)}
        onSubmit={submitPackagingTimeline}
      >
        <div>
          The {joinWithAnd(unassignedOptionalSegmentsLabels)} {unassignedOptionalSegmentsLabels.length > 1 ? 'have' : 'has'} no source selected. Are
          you sure you want to complete?
        </div>
      </Dialog>
    </div>
  );
};

export default Packaging;

const PackagingHeaderContainer = styled(Container)`
  padding: ${({ theme }) => theme.spacing(3)} 0;
`;

const StyledWarningIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.warning.main};
  margin-right: ${({ theme }) => theme.spacing(1)};
`;
const TemplateControls = styled(Grid)`
  .MuiTypography-h4 {
    font-weight: normal;
    opacity: 0.5;
    font-size: 1.3rem;
  }
`;

const StyledSpinner = styled(Spinner)`
  z-index: 999;
`;

const StyledPosterDisplay = styled(PosterDisplay)`
  div {
    padding: 5px;
    img {
      padding: 0px;
    }
  }
`;

const TitleDescription = styled.div`
  display: flex;
  flex-direction: column;
`;

const Rating = styled.div`
  background: ${({ theme }) => theme.palette.secondary.main + '99'};
  border-radius: 2px;
  width: fit-content;
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  font-size: 0.85rem;
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;
